/**
 *  @file     events/event-subscriber.js
 *  @author   Ryan Rossiter <ryan@kingsds.network>
 *  @date     March 2020
 * 
 *  This file is the client-side companion to the event-router.
 *  It maintains a map of subscription tokens that the event router has provisioned
 *  for it, and calls the associated callbacks when the event router emits a new event.
 */

'use strict';
const protocol = require('dcp/protocol');
const debugging = require('dcp/debugging').scope('event-subscriber');
const { leafMerge } = require('dcp/utils');
const { assert } = require('dcp/common/dcp-assert');

/** @typedef {import('./common-types').DcpEvent} DcpEvent */
/** @typedef {import('./common-types').SubscriptionToken} SubscriptionToken */
/** @typedef {import('./common-types').EventLabel} EventLabel */
/** @typedef {import('./common-types').SubscriptionOptions} SubscriptionOptions */


/**
 * A container for callback and SubscriptionToken pairs
 * @typedef {Object} CallbackHandle
 * @property {SubscriptionToken} token
 * @property {function} callback
 */

/**
 * @typedef {Object} Subscription
 * @property {EventLabel} label - Event label
 * @property {function[]} callbacks - The callbacks that will be invoked when an event is received for this subscription
 * @property {SubscriptionOptions} options - Additional options
 * @property {string} optionsStr - A string used to memoize stringifying the options object
 */

/**
 * @constructor
 * @param {object}      options         configuration options for this instance of the EventSubscriber. Default
 *                                      options are given via dcpConfig.eventSubscriber.
 */
class EventSubscriber
{
  constructor(eventEmitter, options)
  {
    this.options = options = leafMerge(options, dcpConfig.eventSubscriber);
    this.eventEmitter = eventEmitter;
    this.subscriptions = new Map();
    this.seenEventIds = [];
    this.nextSeenEventIdSlot = 0;
    /** @type {Array<{id: number, operation: string, payload: any}>} */
    this.pendingTx = [];
    this.nextPendingId = 0;
    
    this.onEventRouterConnectionInterrupted = () => {
      this.openEventRouterConn();
      this.setupEventRouterConnectionEvents();
      this.reestablishSubscriptions();
    }

    const ceci = this;

    this.eventRouterConnection = null;
    this.openEventRouterConn = function openEventRouterConn()
    {
      ceci.eventRouterConnection = new protocol.Connection(dcpConfig.scheduler.services.eventRouter);
      ceci.eventRouterConnection.on('end', ceci.onEventRouterConnectionInterrupted);
    }
    this.openEventRouterConn();
    this.setupEventRouterConnectionEvents();
  }

  async close() {
    debugging() && console.log(`event-subscriber: closing EventSubscriber connection`);

    let subs = this.subscriptions.entries();
    let labels = [];
    const ps = [];
    for (let [label] of subs)
      labels.push(label);
    for (let label of labels)
      ps.push(this.unsubscribe(label));
    await Promise.all(ps);

    this.eventRouterConnection.off('end', this.onEventRouterConnectionInterrupted);

    debugging() && console.log('event-subscriber: closing event-router connection...');
    this.eventRouterConnection.close();
    this.subscriptions.clear();
    this.hookedEventRouterConnectionEvents = false;
  }

  async reestablishSubscriptions() {
    let subs = Array.from(this.subscriptions.entries());
    debugging() && console.log(`event-subscriber: reestablishing ${this.subscriptions.size} subscriptions`);

    const eventTypes = require('dcp/common/dcp-events').eventTypes;
    const reliableEvents = [];
    const optionalEvents = [];
    for (let [label, oldToken] of subs) {
      if (oldToken.options)
        await this.provisionSubscriptionToken(label, oldToken.options);
      else if (eventTypes[label] && eventTypes[label].reliable)
        reliableEvents.push(label);
      else
        optionalEvents.push(label);
    }
    this.subscriptions.clear();

    try
    {
      if (reliableEvents.length || optionalEvents.length) {
        const message = {
          reliableEvents,
          optionalEvents,
          options: this.options
        }

        await this.provisionSubscriptionTokens(message);
      }
      
      // Reset this.pendingTx since all the functions that we want to replay will re-push messages to it
      const messages = this.pendingTx;
      this.pendingTx = [];
      // some operations might have been interupted while the connection is down, replay them
      for (const message of messages)
      {
        switch (message.operation)
        {
          case 'subscribe':
          {
            const payload = message.payload;
            assert(payload.label);
            assert(payload.options)
            await this.provisionSubscriptionToken(payload.label, payload.options);
            break;
          }
          case 'subscribeMany':
          {
            const payload = message.payload;
            assert(payload.message);
            await this.provisionSubscriptionTokens(payload.message);
            break;
          }
          case 'unsubscribe':
          {
            const payload =  message.payload;
            assert(payload.label);
            assert(payload.options)
            await this.releaseSubscriptionToken(payload.label, payload.options);
            break;
          }
          default:
            throw new Error('Looks like you added a new function in event-subscriber without updating here');
        }
      }
    }
    catch (error)
    {
      debugging() && console.warn('event-subscriber: error establishing subscriptions', error);
    }
  }

  setupEventRouterConnectionEvents() {
    debugging() && console.log(`event-subscriber: setupEventRouterConnectionEvents`)
    this.eventRouterConnection.on('request', (req) => {
      const { operation } = req.payload;
      if (operation === 'event') this.onEventRequest(req);
      else req.respond(new Error(`Unknown EventSubscriber operation: ${operation}`));
    });
  }

  async onEventRequest(req) {
    debugging('verbose') && console.log(`event-subscriber: onEventRequest`);
    // data = req.payload.data.event
    const { token, event: data } = req.payload.data;
    const event = data.data;
    const eventId = data.eventId;

    debugging() && console.debug(`131: event ${eventId} (${event.eventName}):`, req.payload.data);
    if (this.subscriptions.has(event.eventName))
    {
      const { token: eventToken } = this.subscriptions.get(event.eventName);
      debugging('verbose') && console.log(`event-subscriber: event: ${event.eventName}`);
      if (eventToken !== token)
        req.respond(new Error(`No subscription registered for token ${token}`));
      else if (eventId && this.seenEventIds.includes(eventId))
        req.respond(new Error(`This event has already been sent: ${eventId}`));
      else
      {
        const label = event.eventName;
        // on each request, we push the eventIds to keep track of events we have seen.
        // this will be filtered in the re-established events.
        if (eventId)
        {
          this.seenEventIds[this.nextSeenEventIdSlot] = eventId;
          this.nextSeenEventIdSlot = (this.nextSeenEventIdSlot + 1) % Number(this.options.seenListSize);
        }
        
        delete event.eventName;
        if (!this.eventEmitter.eventIntercepts)
          this.eventEmitter.emit(label, event);
        else
        {
          if (this.eventEmitter.eventIntercepts[label])
            this.eventEmitter.eventIntercepts[label](event)
          else
            this.eventEmitter.emit(label, event);
        }
        req.respond('success');
      }
    }
    else
    {
      console.warn(`No subscription registered for label ${event.eventName}`);
      req.respond(new Error(`No subscription registered for token ${event.eventName}`));
    }
  }

  /**
   * Log the message intending to send and the operation/function that it came from, used for state recovery when the connection is down
   * @param {string} operation the operation the request is for, used for replaying them when connection is closed
   * @param {*} payload
   * @returns {Promise<*>} the return value of request
   */
  async logAndSend(operation, payload) {
    const pendingTx = this.pendingTx;
    const id = this.nextPendingId++;
    pendingTx.push({ id, operation, payload });

    const erConn = this.eventRouterConnection;

    // the only reason this can throw is due to protocol level errors
    const ret = await erConn.request(operation, payload);

    // binary search is possible
    pendingTx.splice(pendingTx.findIndex((elem) => elem.id === id), 1);
    return ret;
  }

  /**
   * Registers this subscription with the event router, and saves the returned
   * subscription token into the this.subscriptions map.
   * @param {EventLabel} label
   * @param {SubscriptionOptions} options
   */
  async provisionSubscriptionToken(label, options) {
    debugging('verbose') && console.log(`event-subscriber: provisionSubscriptionToken(${label})`);

    let res = await this.logAndSend('subscribe', {
      label, options,
      }).catch((error) => {
        debugging('verbose') && console.error(`Failed to send subscription request to scheduler for the event ${label}: ${error}.`);
    });
    
    if (res && res.success) {
      const token = res.payload?.token;
      this.subscriptions.set(label, { token, options });
    }

  }

  async provisionSubscriptionTokens(message) {
    debugging('verbose') && console.log(`event-subscriber: provisionSubscriptionTokens(${JSON.stringify(message)})`);

    let res = await this.logAndSend('subscribeMany', message)
      .catch((e) => {
        debugging('verbose') && console.error(`Failed to send subscribeMany request to scheduler: ${e}`);
      });
    
    if (res && res.success) {
      const tokenEventPairs = res.payload.tokens;
      tokenEventPairs.forEach((tokenAndEvent) => {
        const label = tokenAndEvent.event;
        const token = tokenAndEvent.token;
        this.subscriptions.set(label, { token });
      })
    } else if (res && !res.success) {
      let errorMessage = 'Failed to subscribe to events.';
      if (typeof res.payload?.message === 'string')
        errorMessage += ` Caused by: ${res.payload.message}`;
      throw new Error(errorMessage);
    }
    
  }

  /**
   * Unregisters a subscription from the event router
   * @param {EventLabel} label
   * @param {SubscriptionToken} token 
   */
  async releaseSubscriptionToken(label, token) {
    debugging('verbose') && console.log(`event-subscriber: releaseSubscriptionToken(${label})`);

    await this.logAndSend('unsubscribe', {
      label, token,
    }).catch((e) => {
      debugging('verbose') && console.error(`Failed to unsubscribe from event ${label}: ${e}.`)
    });
  }

  /**
   * @param {EventLabel} label - Event label
   * @param {function} callback
   * @param {SubscriptionOptions} options - Additional options
   */
  async subscribeManyEvents(reliableEvents, optionalEvents, options={}) {
    this.options = options;
    const noSubReliableEvents = [];
    reliableEvents.forEach((ev) => {
      for (let [, sub] of this.subscriptions)
      {
        // search for a subscription with identical label,
        // to avoid making duplicate subscriptions
        if (ev === sub.label)
        {
          return;
        }
      }
      if (!noSubReliableEvents.includes(ev)) {
        noSubReliableEvents.push(ev);
      }
    })

    const noSubOptionalEvents = [];
    optionalEvents.forEach((ev) => {
      for (let [, sub] of this.subscriptions)
      {
        // search for a subscription with identical label,
        // to avoid making duplicate subscriptions
        if (ev === sub.label)
        {
          return;
        }
      }
      if (!noSubOptionalEvents.includes(ev)) {
        noSubOptionalEvents.push(ev);
      }
    })

    if (reliableEvents.length || optionalEvents.length)
    {
      const message = {
        reliableEvents: noSubReliableEvents,
        optionalEvents: noSubOptionalEvents,
        options,
      }
      debugging() && console.log('event-subscriber: provisioning:', message);
      await this.provisionSubscriptionTokens(message);
    }
  }
  
  async subscribeSchedmsg(subscribeLabels, options={})
  {
    debugging() && console.log(`event-subscriber: subscribe(${subscribeLabels})`);
    const labels = [];
    subscribeLabels.forEach((ev) => {
      // search for a subscription with identical label and filter,
      // to avoid making duplicate subscriptions
      for (let [subLabel, t] of this.subscriptions)
      {
        if (ev === subLabel)
          return;
      }
      labels.push(ev);
    });
    if (labels.length)
    {
      const message = {
        schedmsgEvents: labels,
        options,
      };
      await this.provisionSubscriptionTokens(message);
    }
  }

  async subscribe(label, options={}) {
    debugging() && console.log(`event-subscriber: subscribe(${label})`);
    let token;
    for (let [subLabel, t] of this.subscriptions) {
      // search for a subscription with identical label and filter,
      // to avoid making duplicate subscriptions
      if (label === subLabel) {
        token = t;
        break;
      }
    }

    if (!token) {
      await this.provisionSubscriptionToken(label, options);
    }
  }

  /**
   * @param {string} label
   */
  async unsubscribe(label) {
    debugging() && console.log(`event-subscriber: unsubscribe)${label})`);
    if (!this.subscriptions.has(label)) {
      return;
    }
    
    const token = this.subscriptions.get(label)
    await this.releaseSubscriptionToken(label, token);
    this.subscriptions.delete(label);
  }
}

exports.EventSubscriber = EventSubscriber;
